import React from 'react';
import { MessageSquare, RefreshCw, User, Trash2, Edit2, AlertCircle } from 'lucide-react';
import { useAdminFeedbacksPage } from './useAdminFeedbacksPage';
import AdminTabNav from '../AdminPage/AdminTabNav';
import AdminPagination from '../AdminPage/AdminPagination';
import AdminFeedbackNotesModal from './AdminFeedbackNotesModal';
import AdminFeedbackDeleteModal from './AdminFeedbackDeleteModal';
import './AdminFeedbacksPage.css';

const AdminFeedbacksPage: React.FC = () => {
  const {
    feedbacks,
    paginatedFeedbacks,
    currentPage,
    setCurrentPage,
    totalPages,
    itemsPerPage,
    loading,
    error,
    selectedFeedback,
    setSelectedFeedback,
    adminNotes,
    setAdminNotes,
    updating,
    deleteTarget,
    setDeleteTarget,
    deleting,
    loadFeedbacks,
    getFilenameFromUrl,
    handleUpdateNotes,
    handleDeleteFeedback,
  } = useAdminFeedbacksPage();

  return (
    <div className="admin-feedbacks-page">
      <div className="admin-feedbacks-page-bg">
        <div className="admin-feedbacks-page-bg-1" />
        <div className="admin-feedbacks-page-bg-2" />
      </div>
      <div className="admin-feedbacks-page-inner">
        <AdminTabNav />
        <div className="admin-feedbacks-header">
          <div>
            <h1 className="admin-feedbacks-title">Feedbacks Management</h1>
            <p className="admin-feedbacks-subtitle">Review and manage user feedbacks</p>
          </div>
          <button
            type="button"
            onClick={loadFeedbacks}
            disabled={loading}
            className="admin-feedbacks-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="admin-feedbacks-error">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="admin-feedbacks-loading">
            <div className="admin-feedbacks-loading-spinner" />
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="admin-feedbacks-empty">
            <MessageSquare className="admin-feedbacks-empty-icon w-8 h-8 mx-auto mb-2" />
            <p className="admin-feedbacks-empty-text">No feedbacks found</p>
          </div>
        ) : (
          <>
          <div className="admin-feedbacks-list">
            {paginatedFeedbacks.map((feedback) => (
              <div key={feedback.id} className="admin-feedbacks-card">
                <div className="admin-feedbacks-card-header">
                  <div className="admin-feedbacks-card-main">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="admin-feedbacks-card-title">{feedback.subject}</h3>
                        <div className="admin-feedbacks-card-meta">
                          <User className="w-2.5 h-2.5" />
                          <span>{feedback.user_email}</span>
                          <span>•</span>
                          <span>
                            {new Date(feedback.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="admin-feedbacks-card-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFeedback(feedback);
                            setAdminNotes(feedback.admin_notes || '');
                          }}
                          className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded transition-colors"
                        >
                          <Edit2 className="w-2.5 h-2.5" />
                          <span>Edit Notes</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(feedback)}
                          className="flex items-center space-x-1 px-2 py-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded transition-colors"
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                    <p className="admin-feedbacks-card-message">{feedback.message}</p>
                    {feedback.image_url && (
                      <div className="admin-feedbacks-card-images">
                        {feedback.image_url
                          .split(',')
                          .map((url) => url.trim())
                          .filter(
                            (url) =>
                              url &&
                              (url.startsWith('http://') ||
                                url.startsWith('https://') ||
                                url.startsWith('/api/'))
                          )
                          .map((url, index) => {
                            const imageUrl = url.startsWith('/api/')
                              ? `${process.env.REACT_APP_API_URL || ''}${url}`
                              : url;
                            const fileName = getFilenameFromUrl(imageUrl);
                            return (
                              <div key={index} className="admin-feedbacks-card-image-wrap">
                                <img
                                  src={imageUrl}
                                  alt={`Feedback attachment ${index + 1}`}
                                  className="admin-feedbacks-card-image"
                                  onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <p
                                  className="admin-feedbacks-card-image-filename"
                                  title={fileName}
                                >
                                  {fileName}
                                </p>
                              </div>
                            );
                          })}
                      </div>
                    )}
                    {feedback.admin_notes && (
                      <div className="admin-feedbacks-card-notes">
                        <p className="admin-feedbacks-card-notes-label">Admin Notes:</p>
                        <p className="admin-feedbacks-card-notes-text">{feedback.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {feedbacks.length > 0 && (
            <AdminPagination
              currentPage={Math.min(currentPage, totalPages)}
              totalPages={totalPages}
              totalItems={feedbacks.length}
              itemsPerPage={itemsPerPage}
              itemLabel="feedbacks"
              onPageChange={setCurrentPage}
            />
          )}
          </>
        )}

        {deleteTarget && (
          <AdminFeedbackDeleteModal
            feedback={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteFeedback}
            deleting={deleting}
          />
        )}

        {selectedFeedback && (
          <AdminFeedbackNotesModal
            feedback={selectedFeedback}
            adminNotes={adminNotes}
            onAdminNotesChange={setAdminNotes}
            onClose={() => {
              setSelectedFeedback(null);
              setAdminNotes('');
            }}
            onSave={handleUpdateNotes}
            updating={updating}
            getFilenameFromUrl={getFilenameFromUrl}
          />
        )}
      </div>
    </div>
  );
};

export default AdminFeedbacksPage;
